import React, { useMemo, useState } from "react";
import { Button, Checkbox, Typography } from "@mui/material";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Feature } from "geojson";

import PreferenceFieldButton, { trafficLightData, TrafficLightValue } from "fond/map/Field/PreferenceFieldButton";

import { getPathTypeCounts, getPreferenceCounts } from "./helper";

import { CheckboxRow, Container, Footer, Section, SectionGroup } from "./SelectedPaths.styles";

interface SelectedPathsProps {
  features: Feature[];
  onCancel: () => void;
  onApply: (value: string, includeCrossings: boolean) => void;
}

const SelectedPaths: React.FC<SelectedPathsProps> = ({ features, onCancel, onApply }) => {
  const counts = useMemo(() => getPreferenceCounts(features), [features]);
  const { crossings, paths } = getPathTypeCounts(features);
  const [includeCrossings, setIncludeCrossings] = useState(() => {
    return crossings > 0 && paths === 0;
  });
  const selected = includeCrossings ? paths + crossings : paths;

  const uniformPreference = useMemo(() => {
    const nonZero = ([TrafficLightValue.prefer, TrafficLightValue.neutral, TrafficLightValue.avoid] as const).filter((pref) => counts[pref] > 0);
    if (nonZero.length === 1) {
      return trafficLightData[nonZero[0]].cost.toString();
    }
    return null;
  }, [counts]);

  const [value, setValue] = useState<string | null>(null);
  const activeValue = value ?? uniformPreference;

  const summary = [TrafficLightValue.prefer, TrafficLightValue.neutral, TrafficLightValue.avoid]
    .filter((pref) => counts[pref] > 0)
    .map((pref) => `${counts[pref]} ${trafficLightData[pref].text.toLowerCase()}`)
    .join(" \u00B7 ");

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: string | null) => {
    if (newValue !== null) {
      setValue(newValue);
    }
  };

  return (
    <Container>
      <SectionGroup>
        <div className="section-header">
          <Typography variant="body2" fontWeight={600}>
            Selected paths
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {paths > 0 ? `${paths} path${paths !== 1 ? "s" : ""}` : ""}
            {paths > 0 && crossings > 0 ? " · " : ""}
            {crossings > 0 ? `${crossings} crossing${crossings !== 1 ? "s" : ""}` : ""}
          </Typography>
        </div>
        <div className="section-body">
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
            Currently {summary}
          </Typography>
          <ToggleButtonGroup
            value={activeValue}
            exclusive
            size="small"
            onChange={handleChange}
            sx={{ width: "100%", "& .MuiToggleButton-root": { flex: 1 } }}
          >
            {[TrafficLightValue.neutral, TrafficLightValue.avoid, TrafficLightValue.prefer].map((trafficLightValue) => (
              <PreferenceFieldButton key={trafficLightValue} trafficLightValue={trafficLightValue} disabled={false} value={activeValue ?? ""} />
            ))}
          </ToggleButtonGroup>
        </div>
      </SectionGroup>

      <Section>
        <CheckboxRow>
          <Checkbox disableRipple size="small" checked={includeCrossings} onChange={() => setIncludeCrossings(!includeCrossings)} sx={{ p: 0 }} />
          <div>
            <Typography variant="body2" fontWeight={500}>
              Include crossings
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {crossings} crossing{crossings !== 1 ? "s" : ""} kept as-is
            </Typography>
          </div>
        </CheckboxRow>
      </Section>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {selected} feature{selected !== 1 ? "s" : ""} will be adjusted when you apply
      </Typography>

      <Footer>
        <Button color="primary" size="small" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          sx={{ ml: 1, px: 2 }}
          disabled={activeValue === null}
          onClick={() => activeValue && onApply(activeValue, includeCrossings)}
        >
          Apply to {selected} item{selected !== 1 ? "s" : ""}
        </Button>
      </Footer>
    </Container>
  );
};

export default SelectedPaths;
